import { DateTime } from "luxon";

// ============================================================
// Constants
// ============================================================

export const DEFAULT_EASE_FACTOR = 2.5;
export const DEFAULT_START_INTERVAL = 0;
export const DEFAULT_REPEAT_INTERVAL = 2.5;

export const UserHeaders = {
  Hash: "x-init-data",
  Platform: "x-platform",
} as const;

export const links = {
  landing: "https://memocard.org",
  appBrowser: "https://app.memocard.org",
  telegram: "https://t.me/memo_card_bot",
  telegramChannel: "https://t.me/memo_card_channel",
  botChannel: "https://t.me/memo_card_channel",
  ruBotChannel: "https://t.me/memo_card_channel_ru",
  youtubeRu: "https://www.youtube.com/@memocard_ru",
  youtubeChannelRu: "https://www.youtube.com/@memocard_ru",
  youtubeChannelEn: "https://www.youtube.com/@memocard",
  support: "https://t.me/memo_card_support",
  supportChat: "https://t.me/memo_card_support",
  supportEmail: "support@memocard.org",
  telegramRuVideosFirst: "https://www.youtube.com/@memocard_ru",
  privacyPolicy: "https://memocard.org/privacy",
  privacyPolicyPath: "/privacy",
  termsOfService: "https://memocard.org/terms",
  tosPath: "/terms",
  github: "https://github.com/kubk/memo-card",
  lsqMonthlySubscription: "https://memocard.lemonsqueezy.com/buy/monthly",
  lsqYearlySubscription: "https://memocard.lemonsqueezy.com/buy/yearly",
} as const;

export const landingLanguages = ["en", "ru"] as const;

// ============================================================
// Enums & Literal Types
// ============================================================

export type CardReviewType = "new" | "repeat";

export type ReviewOutcome = "again" | "hard" | "good" | "easy" | "never";

export type CardAnswerType = "remember" | "choice_single";

export type Feature =
  | "ai_mass_creation"
  | "ai_single_card"
  | "ai_speech"
  | "duplicate_deck"
  | "reverse_cards";

export type LanguageShared =
  | "en"
  | "ru"
  | "uk"
  | "es"
  | "pt-br"
  | "fa"
  | "ar";

// Const object so enumValues(LanguageCatalogItemAvailableIn) works in frontend
export const LanguageCatalogItemAvailableIn = {
  En: "en",
  Ru: "ru",
  Uk: "uk",
  Es: "es",
  PtBr: "pt-br",
  Fa: "fa",
  Ar: "ar",
} as const;
export type LanguageCatalogItemAvailableIn =
  (typeof LanguageCatalogItemAvailableIn)[keyof typeof LanguageCatalogItemAvailableIn];

export type DeckSpeakFieldEnum = "front" | "back";

// Const object so enumEntries(SpeakLanguage) works in frontend
export const SpeakLanguage = {
  English: "en",
  Russian: "ru",
  Ukrainian: "uk",
  German: "de",
  French: "fr",
  Spanish: "es",
  Portuguese: "pt",
  Chinese: "zh",
  Japanese: "ja",
  Korean: "ko",
  Arabic: "ar",
  Persian: "fa",
  Turkish: "tr",
  Polish: "pl",
  Italian: "it",
  Dutch: "nl",
  Swedish: "sv",
  Hindi: "hi",
  Bengali: "bn",
  Indonesian: "id",
  Vietnamese: "vi",
  Thai: "th",
  USEnglish: "en-US",
} as const;
export type SpeakLanguage =
  (typeof SpeakLanguage)[keyof typeof SpeakLanguage];

/** Object representation of platform data sent as a header */
export type PlatformSchemaType = {
  platform: string;
  colorScheme: string;
  tgVersion?: string;
};

/** UserSource as a const object so it can be used both as type and value */
export const UserSource = {
  Telegram: "telegram",
  Google: "google",
  Browser: "browser",
} as const;
export type UserSource = (typeof UserSource)[keyof typeof UserSource];

export enum PaymentMethodType {
  Stars = "stars",
  Usd = "usd",
}

export type PlanDuration = 1 | 6 | 12;

// ============================================================
// DB Types
// ============================================================

export type CardAnswerDbType = {
  id: string;
  text: string;
  isCorrect: boolean;
};

export type DeckCardOptionsDbType = {
  voice?: string | null;
  speakField?: DeckSpeakFieldEnum | null;
} | null;

export type DeckCardDbType = {
  id: number;
  deckId: number;
  front: string;
  back: string;
  example: string | null;
  createdAt: string;
  answerType: CardAnswerType;
  answers: CardAnswerDbType[] | null;
  options: DeckCardOptionsDbType;
};

export type DeckWithCardsDbType = {
  id: number;
  authorId: number;
  name: string;
  description: string | null;
  isPublic: boolean;
  shareId: string;
  cardInputModeId: string | null;
  speakField: DeckSpeakFieldEnum | null;
  speakLocale: SpeakLanguage | null;
  speakAutoAi: boolean;
  reverseCards: boolean;
  createdAt: string;
  availableIn: LanguageCatalogItemAvailableIn | null;
  deckCategory: { name: string; logo: string | null } | null;
  categoryId: string | null;
  deckCards: DeckCardDbType[];
  /** Optional: folder the deck belongs to */
  folderId?: number | null;
  folderTitle?: string | null;
};

export type CardToReviewDbType = {
  id: number;
  deckId: number;
  type: CardReviewType;
  interval: number;
  easeFactor: number;
};

export type FolderWithDeckIdDbType = {
  folder_id: number;
  deck_id: number;
  folder_author_id: number;
  folder_description: string | null;
  folder_share_id: string;
  folder_title: string;
};

export type FolderWithDecksWithCards = {
  id: number;
  title: string;
  description: string | null;
  authorId: number;
  shareId: string;
  isPublic: boolean;
  decks: DeckWithCardsDbType[];
};

export type CatalogFolderDbType = {
  id: number;
  title: string;
  /** Alias for title — used by DeckListItemWithDescription */
  name: string;
  description: string | null;
  authorId: number;
  shareId: string;
  isPublic: boolean;
  availableIn: LanguageCatalogItemAvailableIn | null;
  categoryId: string | null;
};

export type UserDbType = {
  id: number;
  telegramId: number | null;
  firstName: string;
  lastName: string | null;
  username: string | null;
  languageCode: string | null;
  /** Override language code (set after user manually changes language) */
  forceLanguageCode?: string | null;
  isAdmin: boolean;
  isSpeakingCardEnabled: boolean;
  isRemindEnabled: boolean;
  lastRemindedDate: string | null;
  createdAt: string;
  source: UserSource | null;
  lastActivity: string | null;
};

/** Request body for the userSettings mutation — matches user-settings-store */
export type UserSettingsRequest = {
  isRemindNotifyEnabled?: boolean;
  isSpeakingCardEnabled?: boolean;
  language?: string | null;
  remindNotificationTime?: string;
};

export type PlansForUser = Array<{
  plan_id: string | null;
  until_date: string | null;
}>;

export type MyInfoResponse = {
  user: UserDbType;
  plans: PlansForUser;
  myDecks: DeckWithCardsDbType[];
  publicDecks: DeckWithCardsDbType[];
  cardsToReview: CardToReviewDbType[];
  folders: FolderWithDeckIdDbType[];
};

export type CardInputModeDb = {
  id: string;
  name: string;
  title: string;
  prompt: string;
  front: string;
  back: string;
  example: string;
  authorId: number | null;
  frontLanguage: string;
  backLanguage: string;
  withExample: boolean;
  previewFront: string;
  previewBack: string;
  previewExample: string;
  options?: { preview_recommend_for?: string } | null;
};

/** CatalogItem is a discriminated union with a .data property containing the full record */
export type CatalogItem =
  | {
      type: "deck";
      id: number;
      title: string;
      description: string | null;
      authorId: number;
      shareId: string;
      language: LanguageShared | null;
      categoryId: string | null;
      data: DeckWithCardsDbType;
    }
  | {
      type: "folder";
      id: number;
      title: string;
      description: string | null;
      authorId: number;
      shareId: string;
      language: LanguageShared | null;
      categoryId: string | null;
      data: CatalogFolderDbType;
    };

export type PlanDb = {
  id: string;
  type: "pro";
  price: number;
  priceStars: number;
  duration: PlanDuration;
  features: Feature[];
};

// ============================================================
// Utility Functions
// ============================================================

export function assert(
  condition: unknown,
  message?: string,
): asserts condition {
  if (!condition) {
    throw new Error(message ?? "Assertion failed");
  }
}

export function canDuplicateDeckOrFolder(
  userId: number,
  item: { authorId: number },
  isPaid: boolean,
): boolean {
  if (item.authorId === userId) {
    return true;
  }
  return isPaid;
}

export function canDeleteItsAccount(user: UserDbType): boolean {
  return !user.isAdmin;
}

export function getUserLanguage(user: UserDbType): LanguageShared {
  const code = user.forceLanguageCode ?? user.languageCode;
  if (!code) return "en";

  const supported: LanguageShared[] = [
    "en",
    "ru",
    "uk",
    "es",
    "pt-br",
    "fa",
    "ar",
  ];
  if (supported.includes(code as LanguageShared)) {
    return code as LanguageShared;
  }
  if (code === "pt") return "pt-br";
  return "en";
}

export function stringToDate(dateStr: string): DateTime {
  return DateTime.fromISO(dateStr);
}

// ============================================================
// SRS — Spaced Repetition (exported for frontend preview use)
// ============================================================

function _calcNextReview(
  outcome: ReviewOutcome,
  currentInterval: number,
  currentEaseFactor: number,
): { interval: number; easeFactor: number } {
  let interval = currentInterval;
  let easeFactor = currentEaseFactor;

  switch (outcome) {
    case "again":
      interval = 0;
      easeFactor = Math.max(1.3, easeFactor - 0.2);
      break;
    case "hard":
      interval = Math.max(1, Math.round(currentInterval * 1.2));
      easeFactor = Math.max(1.3, easeFactor - 0.1);
      break;
    case "good":
      interval =
        currentInterval < 1 ? 1 : Math.round(currentInterval * easeFactor);
      break;
    case "easy":
      interval =
        currentInterval < 1
          ? 4
          : Math.round(currentInterval * easeFactor * 1.3);
      easeFactor = easeFactor + 0.15;
      break;
    case "never":
      interval = 36500;
      break;
  }

  return { interval, easeFactor };
}

/**
 * Client-side preview of next review interval.
 * The `now` parameter is accepted but not used — pass DateTime.now() or new Date().
 */
export function reviewCard(
  _now: unknown,
  interval: number,
  outcome: ReviewOutcome,
  easeFactor: number,
): { interval: number; easeFactor: number } {
  return _calcNextReview(outcome, interval, easeFactor);
}

// ============================================================
// Language Utilities
// ============================================================

export const languagesShared: LanguageShared[] = [
  "en",
  "ru",
  "uk",
  "es",
  "pt-br",
  "fa",
  "ar",
];

export function languageSharedToHuman(lang: LanguageShared): string {
  const names: Record<LanguageShared, string> = {
    en: "English",
    ru: "Russian",
    uk: "Ukrainian",
    es: "Spanish",
    "pt-br": "Portuguese",
    fa: "Persian",
    ar: "Arabic",
  };
  return names[lang] ?? lang;
}

export function languageCatalogItemAvailableInToNative(
  lang: LanguageCatalogItemAvailableIn,
): string {
  const nativeNames: Record<LanguageCatalogItemAvailableIn, string> = {
    en: "English",
    ru: "Русский",
    uk: "Українська",
    es: "Español",
    "pt-br": "Português",
    fa: "فارسی",
    ar: "العربية",
  };
  return nativeNames[lang] ?? lang;
}

// ============================================================
// Plans & Pricing Utilities
// ============================================================

export const durationsWithDiscount: {
  duration: PlanDuration;
  discount: number;
  discountStars: number;
}[] = [
  { duration: 6, discount: 0.2, discountStars: 0.2 },
  { duration: 12, discount: 0.3, discountStars: 0.3 },
];

export function calcPlanPriceForDuration(
  method: PaymentMethodType,
  plan: PlanDb,
  duration: PlanDuration,
): number {
  const basePrice =
    method === PaymentMethodType.Stars ? plan.priceStars : plan.price;
  return basePrice * duration;
}

export function formatPriceAsText(
  price: number,
  method: PaymentMethodType,
): string {
  if (method === PaymentMethodType.Stars) {
    return `${price} Stars`;
  }
  return `$${price.toFixed(2)}`;
}

export function formatDiscountAsText(
  discount: number,
  _lang?: LanguageShared,
): string {
  return `-${Math.round(discount * 100)}%`;
}

export function translateProDuration(
  duration: PlanDuration,
  lang: LanguageShared,
  _method?: PaymentMethodType,
): string {
  const translations: Record<LanguageShared, Record<PlanDuration, string>> = {
    en: { 1: "1 month", 6: "6 months", 12: "1 year" },
    ru: { 1: "1 месяц", 6: "6 месяцев", 12: "1 год" },
    uk: { 1: "1 місяць", 6: "6 місяців", 12: "1 рік" },
    es: { 1: "1 mes", 6: "6 meses", 12: "1 año" },
    "pt-br": { 1: "1 mês", 6: "6 meses", 12: "1 ano" },
    fa: { 1: "۱ ماه", 6: "۶ ماه", 12: "۱ سال" },
    ar: { 1: "شهر واحد", 6: "٦ أشهر", 12: "سنة واحدة" },
  };
  return (translations[lang] ?? translations.en)[duration];
}

export function translateProDescription(
  lang: LanguageShared,
): { title: string; description: string }[] {
  const descriptions: Record<
    LanguageShared,
    { title: string; description: string }[]
  > = {
    en: [
      {
        title: "AI Card Generation",
        description: "Generate cards with AI from any text",
      },
      {
        title: "AI Speech",
        description: "Text-to-speech for card reading",
      },
      {
        title: "Duplicate Decks",
        description: "Copy any public deck to your library",
      },
    ],
    ru: [
      {
        title: "AI Генерация карточек",
        description: "Создавайте карточки с помощью AI из любого текста",
      },
      {
        title: "AI Озвучка",
        description: "Синтез речи для чтения карточек",
      },
      {
        title: "Дублирование колод",
        description: "Скопируйте любую публичную колоду в свою библиотеку",
      },
    ],
    uk: [
      {
        title: "AI Генерація карток",
        description: "Створюйте картки за допомогою AI з будь-якого тексту",
      },
      {
        title: "AI Озвучка",
        description: "Синтез мови для читання карток",
      },
      {
        title: "Дублювання колод",
        description: "Скопіюйте будь-яку публічну колоду до своєї бібліотеки",
      },
    ],
    es: [
      {
        title: "Generación de tarjetas con IA",
        description: "Genera tarjetas con IA de cualquier texto",
      },
      {
        title: "Voz con IA",
        description: "Síntesis de voz para leer tarjetas",
      },
      {
        title: "Duplicar mazos",
        description: "Copia cualquier mazo público a tu biblioteca",
      },
    ],
    "pt-br": [
      {
        title: "Geração de cartões com IA",
        description: "Gere cartões com IA de qualquer texto",
      },
      {
        title: "Voz com IA",
        description: "Síntese de voz para ler cartões",
      },
      {
        title: "Duplicar baralhos",
        description: "Copie qualquer baralho público para sua biblioteca",
      },
    ],
    fa: [
      {
        title: "تولید کارت با هوش مصنوعی",
        description: "با هوش مصنوعی از هر متنی کارت بسازید",
      },
      {
        title: "گفتار با هوش مصنوعی",
        description: "سنتز گفتار برای خواندن کارت‌ها",
      },
      {
        title: "تکثیر دسته‌ها",
        description: "هر دسته عمومی را به کتابخانه خود کپی کنید",
      },
    ],
    ar: [
      {
        title: "توليد البطاقات بالذكاء الاصطناعي",
        description: "أنشئ بطاقات بالذكاء الاصطناعي من أي نص",
      },
      {
        title: "الصوت بالذكاء الاصطناعي",
        description: "تحويل النص إلى كلام لقراءة البطاقات",
      },
      {
        title: "نسخ المجموعات",
        description: "انسخ أي مجموعة عامة إلى مكتبتك",
      },
    ],
  };
  return descriptions[lang] ?? descriptions.en;
}

export const sharedProTitle = "MemoCard Pro";

// ============================================================
// Translator Class
// ============================================================

export class Translator<
  TLang extends string,
  TTranslation extends Record<string, string>,
> {
  translations: Record<TLang, TTranslation>;
  getLang: () => TLang;

  constructor(
    translations: Record<TLang, TTranslation>,
    getLang: () => TLang,
  ) {
    this.translations = translations;
    this.getLang = getLang;
  }

  translate(key: keyof TTranslation, defaultValue?: string): string {
    const lang = this.getLang();
    const translation = this.translations[lang];
    if (!translation) {
      return defaultValue ?? String(key);
    }
    return (translation[key] as string) ?? defaultValue ?? String(key);
  }
}

// ============================================================
// RouterOutput helper type (used by catalog-settings-store)
// ============================================================

export type RouterOutput = {
  catalogItemSettings: {
    get: {
      isPublic: boolean;
      availableIn: string | null;
      categoryId: string | null;
    };
  };
};
